/**
 * Move trainees to another course date.
 *
 * Opened either from the bulk bar (ticked rows) or from a single row's
 * "Move" arrow. Upcoming courses are listed first (past hidden behind a
 * toggle), searchable by name/date, with brand chips and register count.
 * Payments stay on the registration — only `course_id` changes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  X, ArrowRight, Loader2, Calendar as CalendarIcon, Users, GraduationCap, Search, History, Mail, MessageSquare,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import API_URL from '@/config/api';

const BRAND = {
  probound: ['ProBound', 'bg-orange-100 text-orange-800'],
  proepoxy: ['ProEpoxy', 'bg-sky-100 text-sky-800'],
  prodecor: ['ProDecor', 'bg-fuchsia-100 text-fuchsia-800'],
};
const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => {
  if (!iso) return 'No date';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

function CourseOption({ c, selected, onPick }) {
  return (
    <li
      className={`px-3 py-2 cursor-pointer flex items-center gap-3 ${selected ? 'bg-sky-50' : 'hover:bg-gray-50'}`}
      onClick={onPick}
      data-testid={`move-target-${c.id}`}
    >
      <input type="radio" readOnly checked={selected} className="accent-sky-600 shrink-0" aria-label={c.name} />
      <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
        <GraduationCap className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold text-gray-900 text-sm truncate">{c.name}</span>
          {(c.sections || []).map((s) => BRAND[s] && (
            <span key={s} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${BRAND[s][1]}`}>{BRAND[s][0]}</span>
          ))}
        </div>
        <div className="text-xs text-gray-500 flex items-center gap-3 mt-0.5">
          <span className="inline-flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> {fmtDate(c.date)}{c.start_time ? ` · ${c.start_time}` : ''}</span>
          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {c.registrations_total || 0} on register</span>
        </div>
      </div>
    </li>
  );
}

export default function MoveTraineesModal({
  open, onClose, sourceCourseId, sourceCourseName, sourceCourseDate, registrationIds, traineeName, onMoved,
}) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [courses, setCourses] = useState([]);
  const [targetId, setTargetId] = useState('');
  const [q, setQ] = useState('');
  const [showPast, setShowPast] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySms, setNotifySms] = useState(true);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API_URL}/api/staff/training-courses`, { credentials: 'include' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (alive) setCourses((data || []).filter((c) => c.id !== sourceCourseId));
      } catch (e) {
        toast.error('Could not load courses', { description: String(e.message || e).slice(0, 200) });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, sourceCourseId]);

  useEffect(() => {
    if (!open) { setTargetId(''); setQ(''); setShowPast(false); setNotifyEmail(true); setNotifySms(true); }
  }, [open]);

  const count = registrationIds?.length || 0;
  const guestCount = traineeName ? Math.max(0, count - 1) : 0;
  const canSubmit = !busy && !loading && !!targetId && count > 0;

  const { upcoming, past } = useMemo(() => {
    const today = todayIso();
    const needle = q.trim().toLowerCase();
    const match = (c) => !needle || `${c.name || ''} ${c.date || ''} ${fmtDate(c.date)} ${(c.sections || []).join(' ')}`.toLowerCase().includes(needle);
    const rows = courses.filter(match);
    return {
      upcoming: rows.filter((c) => (c.date || '') >= today).sort((a, b) => (a.date || '').localeCompare(b.date || '')),
      past: rows.filter((c) => (c.date || '') < today).sort((a, b) => (b.date || '').localeCompare(a.date || '')),
    };
  }, [courses, q]);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/training-courses/${sourceCourseId}/registrations/move`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registration_ids: registrationIds, target_course_id: targetId, notify_email: notifyEmail, notify_sms: notifySms }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      const target = courses.find((c) => c.id === targetId);
      const targetName = target ? `${target.name} · ${fmtDate(target.date)}` : 'the target course';
      if (data.skipped_count > 0) {
        toast.warning(`Moved ${data.moved_count} · ${data.skipped_count} skipped`, {
          description: (data.skipped || []).map((s) => `${s.name || s.email || s.registration_id}${s.reason === 'email_collision' ? ' — already on target' : ''}`).slice(0, 4).join('; '),
        });
      } else {
        const n = data.notified;
        const sent = n ? [n.email ? `${n.email} email${n.email === 1 ? '' : 's'}` : null, n.sms ? `${n.sms} text${n.sms === 1 ? '' : 's'}` : null].filter(Boolean).join(' + ') : '';
        toast.success(traineeName && data.moved_count === 1 ? `${traineeName} moved to ${targetName}` : `Moved ${data.moved_count} trainee${data.moved_count === 1 ? '' : 's'} to ${targetName}`,
          { description: n ? (sent ? `Notified: ${sent}${(n.email_failed || n.sms_failed) ? ` · ${(n.email_failed || 0) + (n.sms_failed || 0)} failed` : ''}` : 'No notifications could be sent (missing email/mobile)') : undefined });
      }
      if (onMoved) onMoved(data);
      onClose();
    } catch (e) {
      toast.error('Move failed', { description: String(e.message || e).slice(0, 200) });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="move-trainees-modal" onClick={() => !busy && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center shrink-0">
              <ArrowRight className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="font-bold text-gray-900 truncate" data-testid="move-trainees-title">
                {traineeName ? `Move ${traineeName}${guestCount ? ` + ${guestCount} guest${guestCount === 1 ? '' : 's'}` : ''}` : `Move ${count} trainee${count === 1 ? '' : 's'}`}
              </h2>
              <p className="text-xs text-gray-500 truncate">from {sourceCourseName || 'this course'}{sourceCourseDate ? ` · ${fmtDate(sourceCourseDate)}` : ''} — payments and notes come with them</p>
            </div>
          </div>
          <button onClick={() => !busy && onClose()} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="move-trainees-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search" value={q} onChange={(e) => setQ(e.target.value)} autoFocus
              placeholder="Search course name, brand or date…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-sky-400"
              data-testid="move-trainees-search"
            />
          </div>
          {loading ? (
            <div className="text-center py-6 text-sm text-gray-500"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Loading courses…</div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Upcoming dates ({upcoming.length})</span>
                {past.length > 0 && (
                  <button type="button" onClick={() => setShowPast((v) => !v)} className="text-xs font-semibold text-gray-500 hover:text-gray-900 inline-flex items-center gap-1" data-testid="move-trainees-toggle-past">
                    <History className="w-3 h-3" /> {showPast ? 'Hide' : 'Show'} past ({past.length})
                  </button>
                )}
              </div>
              {upcoming.length === 0 ? (
                <div className="text-center py-5 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500" data-testid="move-trainees-empty">
                  {q ? 'No upcoming courses match your search.' : 'No upcoming courses to move to. Create one first.'}
                </div>
              ) : (
                <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto" data-testid="move-trainees-list">
                  {upcoming.map((c) => <CourseOption key={c.id} c={c} selected={c.id === targetId} onPick={() => setTargetId(c.id)} />)}
                </ul>
              )}
              {showPast && past.length > 0 && (
                <>
                  <span className="block text-xs font-bold uppercase tracking-wider text-gray-400">Past dates</span>
                  <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-40 overflow-y-auto opacity-80" data-testid="move-trainees-past-list">
                    {past.map((c) => <CourseOption key={c.id} c={c} selected={c.id === targetId} onPick={() => setTargetId(c.id)} />)}
                  </ul>
                </>
              )}
            </>
          )}
        </div>

        <div className="border-t border-gray-200 px-5 py-3 flex flex-wrap items-center gap-2 bg-gray-50">
          <div className="flex items-center gap-3 mr-auto text-xs font-semibold text-gray-700" data-testid="move-trainees-notify">
            <span className="text-gray-500">Notify trainee{count === 1 ? '' : 's'}:</span>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} className="accent-sky-600" data-testid="move-trainees-notify-email" />
              <Mail className="w-3.5 h-3.5 text-gray-500" /> Email
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={notifySms} onChange={(e) => setNotifySms(e.target.checked)} className="accent-sky-600" data-testid="move-trainees-notify-sms" />
              <MessageSquare className="w-3.5 h-3.5 text-gray-500" /> Text
            </label>
          </div>
          <Button variant="outline" onClick={() => !busy && onClose()} disabled={busy} data-testid="move-trainees-cancel">Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit} className="bg-sky-600 hover:bg-sky-500 text-white" data-testid="move-trainees-submit">
            {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Moving…</> : <>Move {count}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
